import { useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import { Power, RotateCw, Loader2, Terminal as TerminalIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';

type ConnState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

interface PelicanConsoleProps {
  serverId: string;
}

/**
 * Live terminal for a Pelican-panel game server. Speaks WebSocket
 * directly to the proxy at /api/services/pelican/server/:id/console/ws
 * which forwards bytes to Wings.
 *
 * Behaviour:
 *   - xterm.js draws the output; user keystrokes are forwarded upstream.
 *   - On disconnect we render the reason in red and offer a reconnect button.
 *   - The server is responsible for resizing the PTY. We send a
 *     `resize` JSON message whenever the FitAddon resizes the visible
 *     cols/rows, and the upstream forwards the JSON to Wings.
 *   - The xterm styles are imported here (not in globals.css) so the
 *     terminal CSS is only loaded when this component mounts.
 */
export function PelicanConsole({ serverId }: PelicanConsoleProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const [state, setState] = useState<ConnState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Build a singleton terminal once, on mount. Re-build on serverId change.
  useEffect(() => {
    if (!containerRef.current) return;
    const term = new Terminal({
      fontFamily:
        '"JetBrains Mono", "SF Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: 12,
      lineHeight: 1.25,
      cursorBlink: true,
      cursorStyle: 'block',
      convertEol: true,
      scrollback: 5000,
      theme: {
        background: '#0a0a0a',
        foreground: '#d4d4d4',
        cursor: '#fafafa',
        cursorAccent: '#0a0a0a',
        selectionBackground: '#264f78',
        black: '#000000',
        red: '#ef4444',
        green: '#22c55e',
        yellow: '#eab308',
        blue: '#3b82f6',
        magenta: '#a855f7',
        cyan: '#06b6d4',
        white: '#d4d4d4',
        brightBlack: '#525252',
        brightRed: '#f87171',
        brightGreen: '#4ade80',
        brightYellow: '#facc15',
        brightBlue: '#60a5fa',
        brightMagenta: '#c084fc',
        brightCyan: '#22d3ee',
        brightWhite: '#fafafa',
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());
    term.open(containerRef.current);
    fit.fit();

    termRef.current = term;
    fitRef.current = fit;

    term.writeln('\x1b[90mconsole idle — click Connect to start the terminal session\x1b[0m');

    return () => {
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
    };
  }, [serverId]);

  // Re-fit on container resize.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      try {
        fitRef.current?.fit();
      } catch {
        /* terminal not yet attached */
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function buildWsUrl(): string {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}/api/services/pelican/server/${encodeURIComponent(serverId)}/console/ws`;
  }

  function connect() {
    const term = termRef.current;
    if (!term) return;
    if (wsRef.current) wsRef.current.close();
    setState('connecting');
    setErrorMsg(null);
    term.clear();
    term.writeln('\x1b[90mconnecting…\x1b[0m');

    const ws = new WebSocket(buildWsUrl());
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      setState('open');
      term.writeln('\x1b[32mconnected\x1b[0m');
      // Re-fit and send initial resize to the upstream.
      try {
        fitRef.current?.fit();
        const cols = term.cols;
        const rows = term.rows;
        ws.send(JSON.stringify({ event: 'resize', args: [cols.toString(), rows.toString()] }));
      } catch {
        /* noop */
      }
      // Wire up keystroke forwarding.
      term.onData((data) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(new TextEncoder().encode(data));
        }
      });
    };

    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        // JSON control frame from the proxy.
        try {
          const parsed = JSON.parse(ev.data) as
            | { type: 'ready' }
            | { type: 'error'; message: string }
            | { type: 'status'; message: string };
          if (parsed.type === 'ready') {
            term.writeln('\x1b[90mhandshake: ready\x1b[0m');
            return;
          }
          if (parsed.type === 'error') {
            term.writeln(`\x1b[31merror: ${parsed.message}\x1b[0m`);
            setErrorMsg(parsed.message);
            return;
          }
          if (parsed.type === 'status') {
            term.writeln(`\x1b[90m— ${parsed.message}\x1b[0m`);
            return;
          }
        } catch {
          // not JSON — treat as raw text output
          term.write(ev.data);
        }
      } else {
        // Binary frame from Wings: write directly to the terminal.
        term.write(new Uint8Array(ev.data as ArrayBuffer));
      }
    };

    ws.onerror = () => {
      setState('error');
      setErrorMsg('WebSocket error — check proxy logs and PELICAN_CLIENT_KEY env');
      term.writeln('\x1b[31m✗ connection error\x1b[0m');
    };

    ws.onclose = (ev) => {
      setState('closed');
      const reason = ev.reason || `code ${ev.code}`;
      term.writeln(`\x1b[90mdisconnected (${reason})\x1b[0m`);
      // Re-enable input on the terminal even though there's nowhere
      // for keystrokes to go. The connect button is the only way back.
      term.onData(() => undefined);
    };
  }

  function disconnect() {
    wsRef.current?.close();
  }

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      wsRef.current?.close();
    };
  }, []);

  const connected = state === 'open';
  const connecting = state === 'connecting';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <TerminalIcon className="h-3.5 w-3.5" />
          <span>
            {state === 'open' && 'live · streaming from Wings'}
            {state === 'connecting' && 'connecting…'}
            {state === 'closed' && 'disconnected'}
            {state === 'error' && 'error'}
            {state === 'idle' && 'idle'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {!connected && !connecting && (
            <button
              onClick={connect}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
            >
              <Power className="h-3 w-3" />
              connect
            </button>
          )}
          {connecting && (
            <button
              disabled
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-border/30 text-muted-foreground/40 cursor-not-allowed"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              connecting
            </button>
          )}
          {connected && (
            <button
              onClick={disconnect}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
            >
              <X className="h-3 w-3" />
              disconnect
            </button>
          )}
          {connected && (
            <button
              onClick={() => {
                termRef.current?.clear();
                wsRef.current?.send(JSON.stringify({ event: 'send', args: ['clear'] }));
              }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-border text-foreground hover:bg-accent"
              title="Ctrl+L"
            >
              <RotateCw className="h-3 w-3" />
              clear
            </button>
          )}
        </div>
      </div>

      {errorMsg && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-300 font-mono">
          {errorMsg}
        </div>
      )}

      <div
        ref={containerRef}
        className={cn(
          'rounded-md border border-border overflow-hidden',
          'h-[500px] bg-[#0a0a0a]',
        )}
      />
    </div>
  );
}
